import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, ArrowUpRight } from "lucide-react";
import { fetchVideos } from "@/lib/api";

/**
 * Surfaces /watch videos inside a section. Prefers videos tagged with `category`,
 * and falls back to the latest videos so the strip is never empty.
 */
export function VideoStrip({ category, title = "On Screen", limit = 3 }) {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchVideos()
      .then((all) => {
        const tagged = all.filter((v) => v.category === category);
        const list = (tagged.length ? tagged : all).slice(0, limit);
        setVideos(list);
      })
      .catch(() => {});
  }, [category, limit]);

  if (videos.length === 0) return null;

  return (
    <section className="mt-14" data-testid="media-video-strip">
      <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Watch</span>
          <h2 className="font-anton text-3xl md:text-4xl uppercase tracking-tight mt-2 leading-none">{title}</h2>
        </div>
        <Link
          to="/watch"
          data-testid="video-strip-viewall"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-signal transition-colors shrink-0"
        >
          All videos <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {videos.map((v, i) => (
          <div
            key={v.id}
            data-testid={`media-video-${v.id}`}
            className="group border border-white/10 bg-card hover:border-signal transition-colors animate-rise"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <button
              onClick={() => setActive(v)}
              className="relative block w-full aspect-video overflow-hidden"
              aria-label={`Play ${v.title}`}
            >
              <img
                src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                alt={v.title}
                className="img-treatment w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-14 h-14 rounded-full bg-signal/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-white ml-1" fill="white" />
                </span>
              </span>
              <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-signal text-white px-2 py-1">
                {v.category}
              </span>
            </button>
            <div className="p-5">
              <h3 className="font-display text-lg font-semibold tracking-tight leading-tight group-hover:text-signal transition-colors">
                {v.title}
              </h3>
              {v.description && <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{v.description}</p>}
            </div>
          </div>
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={() => setActive(null)}
          data-testid="media-video-modal"
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-anton text-xl uppercase tracking-tight">{active.title}</h3>
              <button
                data-testid="media-video-modal-close"
                onClick={() => setActive(null)}
                className="text-foreground/70 hover:text-signal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="aspect-video w-full border border-white/10">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
                title={active.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
